import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  Grid,
  Paper
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../api';
import Spinner from '../components/Spinner';

const parseAssignmentDueDate = (dueDate: string): Date | null => {
  if (!dueDate) return null;
  const [date, time] = dueDate.split('T');
  if (!time) {
    const [year, month, day] = date.split('-').map((value) => Number(value));
    return new Date(year, month - 1, day, 23, 59, 59);
  }
  return new Date(dueDate);
};

interface AssignmentDetail {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  instructions: string;
  totalPoints: number;
  dueDate: string;
  attachmentUrl: string;
  attachmentName: string;
  attachmentUpload?: {
    data?: string;
    mimeType?: string;
    filename?: string;
    originalName?: string;
  };
  staff: { name: string; email?: string };
}

interface SubmissionDetail {
  _id: string;
  submissionText: string;
  submissionFileUpload?: {
    data?: string;
    mimeType?: string;
    filename?: string;
    originalName?: string;
  };
  submittedAt: string;
}

const StudentAssignmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [assignment, setAssignment] = useState<AssignmentDetail | null>(null);
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionFile, setSubmissionFile] = useState<File | null>(null);
  const [submissionFileUpload, setSubmissionFileUpload] = useState<null | { data: string; mimeType: string; filename: string; originalName: string }>(null);

  useEffect(() => {
    const loadAssignment = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!assignmentId) {
          throw new Error('Invalid assignment identifier');
        }
        const response = await api.get(`/school-work/assignments/${assignmentId}`);
        setAssignment(response.data.assignment);
        setSubmission(response.data.submission || null);
        setSubmissionText(response.data.submission?.submissionText || '');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load assignment details');
      } finally {
        setLoading(false);
      }
    };

    loadAssignment();
  }, [assignmentId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setSubmissionFile(null);
      setSubmissionFileUpload(null);
      return;
    }

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted for assignment upload.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        const base64Data = reader.result.split(',')[1] || '';
        setSubmissionFileUpload({
          data: base64Data,
          mimeType: file.type,
          filename: file.name,
          originalName: file.name
        });
        setSubmissionFile(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!submissionText && !submissionFileUpload) {
      setError('Enter your text answer or upload a PDF file before submitting.');
      return;
    }

    if (!assignmentId) {
      setError('Invalid assignment');
      return;
    }

    if (assignment?.dueDate) {
      const deadline = parseAssignmentDueDate(assignment.dueDate);
      if (deadline && Date.now() > deadline.getTime()) {
        setError('The assignment deadline has passed. You cannot submit after the due date and time.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: any = {
        submissionText: submissionText.trim()
      };
      if (submissionFileUpload) {
        payload.submissionFile = submissionFileUpload;
      }

      const response = await api.post(`/school-work/assignments/${assignmentId}/submit`, payload);
      setSuccess(response.data.message || 'Assignment submitted successfully');
      setSubmission(response.data.submission);
      if (response.data.submission?.submissionText) {
        setSubmissionText(response.data.submission.submissionText);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const assignmentDeadline = assignment?.dueDate ? parseAssignmentDueDate(assignment.dueDate) : null;
  const hasDeadlinePassed = assignmentDeadline ? Date.now() > assignmentDeadline.getTime() : false;

  const assignmentAttachmentUrl = assignment?.attachmentUpload?.data
    ? `data:${assignment.attachmentUpload.mimeType};base64,${assignment.attachmentUpload.data}`
    : assignment?.attachmentUrl || undefined;
  const attachmentDownloadName = assignment?.attachmentName || assignment?.attachmentUpload?.originalName || assignment?.attachmentUpload?.filename || 'assignment.pdf';
  const isDataAttachmentUrl = Boolean(assignmentAttachmentUrl && assignmentAttachmentUrl.startsWith('data:'));

  const submissionFileUrl = submission?.submissionFileUpload?.data
    ? `data:${submission.submissionFileUpload.mimeType};base64,${submission.submissionFileUpload.data}`
    : '';

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/school-work')} sx={{ mb: 3 }}>
        Back to Dashboard
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : assignment ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>{assignment.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Subject: {assignment.subject?.name || 'General'} • Class: {assignment.className || 'All'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Due: {assignmentDeadline ? assignmentDeadline.toLocaleString() : 'No deadline'}
              </Typography>
              <Typography sx={{ mb: 2 }}>{assignment.instructions || assignment.description || 'No instructions provided.'}</Typography>
              {assignmentAttachmentUrl ? (
                <Button
                  component="a"
                  href={assignmentAttachmentUrl}
                  download={isDataAttachmentUrl ? attachmentDownloadName : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{ mb: 2 }}
                >
                  Download Assignment Attachment
                </Button>
              ) : null}
              <Typography variant="caption" color="text.secondary" display="block">
                Assigned by {assignment.staff?.name || 'your teacher'} • Total points: {assignment.totalPoints}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Submit Your Work</Typography>
              {submission ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  You have already submitted this assignment. You may resubmit updated work below.
                </Alert>
              ) : null}
              {hasDeadlinePassed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  The assignment due date and time has passed. Submission is closed.
                </Alert>
              )}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              <TextField
                label="Type your answer"
                fullWidth
                multiline
                minRows={5}
                value={submissionText}
                onChange={(e) => setSubmissionText(e.target.value)}
                sx={{ mb: 2 }}
                disabled={hasDeadlinePassed}
              />
              <input
                id="student-assignment-file-upload"
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="student-assignment-file-upload"
                sx={{ mb: 1 }}
                disabled={hasDeadlinePassed}
              >
                {submissionFile ? 'Change PDF Upload' : 'Upload PDF'}
              </Button>
              {submissionFile ? (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Selected file: {submissionFile.name}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                You can type your response and optionally attach a PDF file.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={submitting || hasDeadlinePassed}
              >
                {submitting ? 'Submitting...' : 'Submit Assignment'}
              </Button>
              {submission && submissionFileUrl ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Previous submission:</Typography>
                  <Typography variant="body2" noWrap>{submission.submissionFileUpload?.originalName || submission.submissionFileUpload?.filename}</Typography>
                  <Button
                    size="small"
                    component="a"
                    href={submissionFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 1 }}
                  >
                    Download Submitted PDF
                  </Button>
                </Box>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Assignment not found.</Alert>
      )}
    </Box>
  );
};

export default StudentAssignmentDetail;
